import { useEffect, useRef, useState } from 'react';
import { Camera, X, Loader2 } from 'lucide-react';
import {
  signAndPutImage,
  resolveUploadUrl,
  UploadFailedError,
} from '@/lib/uploads';
import { useT } from '@/lib/i18n';
import { useApp } from '@/store';
import { Button } from '@/components/ui/button';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

// Lazy-resolve a stored ref (s3:<key> → presigned GET, or a data: URL) to a
// displayable thumbnail src. Kept per-thumbnail so images load on demand
// rather than eagerly resolving every ref up front (5–20 photos per visit).
function Thumb({
  stored,
  onRemove,
  disabled,
}: {
  stored: string;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const t = useT();
  const [src, setSrc] = useState('');
  useEffect(() => {
    let alive = true;
    resolveUploadUrl(stored)
      .then(u => alive && setSrc(u))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [stored]);
  return (
    <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md border bg-muted">
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
      {!disabled && (
        <button
          type="button"
          onClick={onRemove}
          title={t('common.delete')}
          aria-label={t('common.delete')}
          className="absolute right-0.5 top-0.5 rounded-full bg-background/80 p-0.5 shadow hover:bg-background"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export function PhotoUploader({ value, onChange, disabled }: Props) {
  const t = useT();
  const toast = useApp(s => s.toast);
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const onFiles = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const uploaded = await Promise.all(
        Array.from(files).map(f => signAndPutImage(f))
      );
      onChange([...value, ...uploaded]);
    } catch (err) {
      // Uploads reject rather than inlining a huge data: URL (client #48).
      toast(
        err instanceof UploadFailedError ? t(err.i18nKey) : t('common.error')
      );
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        // hint mobile browsers to offer the rear camera
        capture="environment"
        multiple
        className="hidden"
        onChange={e => onFiles(e.target.files)}
      />
      <div className="flex flex-wrap gap-2">
        {value.map((stored, i) => (
          <Thumb
            key={`${stored}-${i}`}
            stored={stored}
            disabled={disabled}
            onRemove={() => onChange(value.filter((_, j) => j !== i))}
          />
        ))}
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            className="h-20 w-20 flex-col gap-1 p-0"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Camera className="h-5 w-5" />
            )}
            <span className="text-[10px]">{t('photos.add')}</span>
          </Button>
        )}
      </div>
      {value.length === 0 && disabled && (
        <p className="text-xs text-muted-foreground">{t('photos.none')}</p>
      )}
    </div>
  );
}
